<template>
  <div class="osk-sizes-info">
    <div class="osk-sizes-info__content">
      <div class="osk-sizes-info__title">
        {{ $t('components.product.sizes.sizesInfo.title') }}
      </div>

      <div class="osk-sizes-info__subtitle">
        {{ $t('components.product.sizes.sizesInfo.subtitle') }}
      </div>

      <UikitLoader
        v-if="pending"
        class="osk-sizes-info__loader"
      />

      <template v-else>
        <div class="osk-sizes-info__size-name">
          {{ currentType?.sizeTypeDescription }} - {{ currentType?.sizeType }}
        </div>

        <div class="osk-sizes-info__buttons">
          <div
            v-for="size in currentTypeSizes"
            :key="size.id"
            :class="{
              'osk-sizes-info__button': true,
              'osk-sizes-info__button_in-stock': size.isInStock,
              'osk-sizes-info__button_active': sizeChosenId === size.id,
            }"
            @click="chooseSize(size.id)"
          >
            {{ size.name }}
          </div>
        </div>

        <div
          v-if="comparedSizes?.length"
          class="osk-sizes-info__compared"
        >
          <div class="osk-sizes-info__compared-title">
            {{ $t('components.product.sizes.sizesInfo.comparedTitle') }}
          </div>

          <div
            v-for="(comparedSize, index) in comparedSizes"
            :key="index"
            class="osk-sizes-info__compared-item"
          >
            <div class="osk-sizes-info__compared-item-name">
              {{ comparedSize.sizeTypeDescription }} <span>({{ comparedSize.sizeType }})</span>
            </div>

            <div class="osk-sizes-info__compared-item-size">
              {{ comparedSize.values[0].name }}
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="osk-sizes-info__footer">
      <UikitButton
        full-width
        :disabled="(!chosenInStock || pending)"
        @click="handleApply"
      >
        {{ $t('common.ready') }}
      </UikitButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  SizeValueDTORes, SizeTypeDTO, SizeDTO, OrderPositionDTORes,
} from '~/restAPI/data-contracts';
import { SizeWithStockFlag } from '~/types/components/sizeModal';
import { useProductService } from '~/services/product';

const props = defineProps<{
  product?: OrderPositionDTORes
  size?: SizeValueDTORes
  sizeTypes?: SizeTypeDTO[] | null
}>();

const emits = defineEmits<{
  (e: 'apply', value: SizeValueDTORes): void,
}>();

const categoryId = computed(() => props.product?.categoryId);
const sizes = computed(() => props.product?.sizes ?? []);
const sizeChosen = computed(() => props.size ?? (props.product?.sizes && props.product?.sizes[0]));

const sizeChosenId = ref(sizeChosen.value ? sizeChosen.value.id : sizes.value[0].id);

const { getSizes } = useProductService();

const {
  data: sizeTypes,
  pending,
  execute: getSizesRequest,
} = useLazyAsyncData(() => {
  if (props.sizeTypes) return Promise.resolve(props.sizeTypes);
  if (!categoryId.value) return Promise.resolve([]);

  return getSizes(categoryId.value);
}, { immediate: false });

const currentTypeId = computed(() => sizes.value[0].categorySizeType);
const currentType = computed(() => sizeTypes.value?.find(({ sizeType }: SizeTypeDTO) => sizeType === currentTypeId.value));
const currentTypeSizes = computed<SizeWithStockFlag[]>(() => (currentType.value?.values
  ? currentType.value.values // .filter((typeSize: SizeDTO) => typeSize.name !== 'U')
    .map((typeSize: SizeDTO) => {
      const currentSize = sizes.value.find((size) => size.id === typeSize.id);

      const isInStock = (currentSize?.count ?? 0) > 0;

      return {
        ...typeSize,
        isInStock,
      };
    })
  : []));

const comparedSizes = computed(
  () => sizeTypes.value
    ?.map((sizeType:SizeTypeDTO) => {
      const filtered = sizeType!.values!.filter((size:SizeDTO) => size.id === sizeChosenId.value); // && size.name !== 'U'

      return {
        ...sizeType,
        values: filtered,
      };
    })
    .filter((sizeType:SizeTypeDTO) => sizeType.sizeType !== currentTypeId.value && sizeType.values?.length),
);

const chosenInStock = computed(
  () => sizes.value.find((size) => size.id === sizeChosenId.value && (size.count ?? 0) > 0),
);

function chooseSize(sizeId?: number) {
  sizeChosenId.value = sizeId;
}

function handleApply() {
  emits('apply', chosenInStock.value!);
}

watch(() => categoryId.value, async () => {
  if (!categoryId.value) return;

  getSizesRequest();
}, { immediate: true });
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-sizes-info {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 50px;

  @include media-query(md-and-up) {
    padding-top: 100px;
  }

  &__content {
    overflow-y: scroll;
    height: 100%;
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    &::-webkit-scrollbar {
      display: none;
    }

    @include media-query(md-and-up) {
      padding: 0 52px;
      max-width: 504px;
    }
  }

  &__loader {
    margin: $indent-xlarge auto 0;
  }

  &__footer {
    padding-top: $indent-medium;

    @include media-query(md-and-up) {
      padding: $indent-medium 52px $indent-mmedium;
    }
  }

  &__title {
    @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);
    margin-bottom: 18px;

    @include media-query(md-and-up) {
      margin-bottom: $indent-compact;
    }
  }

  &__subtitle {
    @include font-style($font-size-caption, $line-height-caption, $font-weight-medium);
    margin-bottom: 49px;
    color: $grey;

    @include media-query(md-and-up) {
      margin-bottom: 42px;
    }
  }

  &__size-name {
    @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);
    font-weight: $font-weight-bold;
    margin-bottom: 26px;

    @include media-query(md-and-up) {
      @include font-style($font-size-body, $line-height-body, $font-weight-bold);
    }
  }

  &__buttons {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 34px;

    @include media-query(md-and-up) {
      margin-right: 0;
      margin-bottom: 42px;
    }
  }

  &__button {
    width: 62px;
    height: 52px;
    border: 0.5px solid $grey-2;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    @include media-query(md-and-up) {
      width: 52px;
    }

    &_in-stock {
      border-color: $black;
    }

    &_active {
      background: $black;
      color: $white;
    }
  }

  &__compared {
    width: 100%;
  }

  &__compared-title {
    @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);
    margin-bottom: 18px;

    @include media-query(md-and-up) {
      @include font-style($font-size-body, $line-height-body, $font-weight-bold);
      margin-bottom: 10px;
    }
  }

  &__compared-item {
    padding: $indent-medium 0;
    display: flex;
    justify-content: space-between;

    &:not(:last-child) {
      border-bottom: 0.5px solid $grey-3;
    }
  }
}
</style>
